<template>
  <section class="n-section-secondary" :style="`background-color: var(--c-${vars.backgroundColor})`">
    <!-- v1 -->
    <div v-if="variants.v1 === variant" class="tw-flex tw-w-full tw-flex-col lg:tw-flex-row lg:tw-justify-center">
      <div v-for="(count, index) in counts" :key="`countsection-count-${index}`" class="tw-flex tw-flex-col tw-items-center tw-justify-start tw-break-all tw-py-6 tw-text-center md:tw-py-10 lg:tw-w-full lg:tw-py-20 2xl:tw-py-24" ref="countElements">
        <NuxtImg v-if="count.icon" :src="count.icon" class="tw-mb-7 tw-w-16" />
        <div class="tw-flex tw-flex-row tw-items-center tw-text-[var(--c-primary)]">
          <h3 v-if="count.prefix">{{ count.prefix }}</h3>
          <h3 class="odometer tw-whitespace-nowrap">0</h3>
          <h3 v-if="count.suffix">{{ count.suffix }}</h3>
        </div>
        <p class="tw-mx-6 tw-min-h-[48px]">{{ count.description }}</p>
      </div>
    </div>

    <!-- v2 -->
    <div v-if="variants.v2 === variant" class="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center">
      <h2 class="tw-mb-3">{{ vars.titleText }}</h2>
      <div class="tw-flex tw-w-full tw-flex-col lg:tw-flex-row lg:tw-justify-center">
        <div v-for="(count, index) in counts" :key="`countsection-count-${index}`" class="tw-flex tw-flex-col tw-items-center tw-justify-start tw-break-all tw-py-6 tw-text-center md:tw-py-10 lg:tw-w-full lg:tw-py-20 2xl:tw-py-24" ref="countElements">
          <NuxtImg v-if="count.icon" :src="count.icon" class="tw-mb-7 tw-w-16" />
          <div class="tw-flex tw-flex-row tw-items-center tw-text-[var(--c-primary)]">
            <h3 v-if="count.prefix">{{ count.prefix }}</h3>
            <h3 class="odometer tw-whitespace-nowrap">0</h3>
            <h3 v-if="count.suffix">{{ count.suffix }}</h3>
          </div>
          <p class="tw-mx-6 tw-min-h-[48px]">{{ count.description }}</p>
        </div>
      </div>
    </div>

    <!-- v3 -->
    <div v-if="variants.v3 === variant" class="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center">
      <h6 class="tw-mb-3">{{ vars.titleText }}</h6>
      <div class="tw-flex tw-w-full tw-flex-col lg:tw-flex-row lg:tw-justify-center">
        <div v-for="(count, index) in counts" :key="`countsection-count-${index}`" class="tw-flex tw-flex-col tw-items-center tw-justify-start tw-break-all tw-py-6 tw-text-center md:tw-py-10 lg:tw-w-full lg:tw-py-20 2xl:tw-py-24" ref="countElements">
          <NuxtImg v-if="count.icon" :src="count.icon" class="tw-mb-7 tw-w-16" />
          <div class="tw-flex tw-flex-row tw-items-center tw-text-[var(--c-primary)]">
            <h1 v-if="count.prefix">{{ count.prefix }}</h1>
            <h1 class="js-count">0</h1>
            <h1 v-if="count.suffix">{{ count.suffix }}</h1>
          </div>
          <h6 class="tw-mx-6 tw-min-h-[48px]">{{ count.description }}</h6>
        </div>
      </div>
    </div>

    <!-- v4  -->
    <div v-if="variants.v4 === variant" class="v4 tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center">
      <h2 class="tw-mb-3">{{ vars.titleText }}</h2>
      <div class="tw-flex tw-w-full tw-flex-col lg:tw-flex-row lg:tw-justify-center">
        <div v-for="(count, index) in counts" :key="`countsection-count-${index}`" class="tw-flex tw-flex-col tw-items-center tw-justify-start tw-break-all tw-py-6 tw-text-center md:tw-py-10 lg:tw-w-full lg:tw-py-20 2xl:tw-py-24" ref="countElements">
          <NuxtImg v-if="count.icon" :src="count.icon" class="tw-mb-7 tw-w-16" />
          <div class="tw-flex tw-flex-row tw-items-center">
            <h1 class="tw-text-[var(--c-primary)] !tw-text-[80px]" v-if="count.prefix">{{ count.prefix }}</h1>
            <h1 class="odometer-v4 tw-whitespace-nowrap !tw-text-[80px]"></h1>
            <h1 class="tw-text-[var(--c-primary)] !tw-text-[80px]" v-if="count.suffix">{{ count.suffix }}</h1>
          </div>
          <h6 class="tw-mx-6 tw-min-h-[48px]">{{ count.description }}</h6>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { ComponentMixin } from '~/mixins/component.mixin';

export default defineNuxtComponent({
  name: 'CountSection',
  mixins: [ComponentMixin],

  data() {
    return {
      variants: { v1: 'v1', v2: 'v2', v3: 'v3', v4: 'v4' },
      observers: [] as IntersectionObserver[],
      animatedElements: new Set<number>(),
    };
  },

  computed: {
    counts() {
      return this.groupedVariables.counts;
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.setupIntersectionObservers();
    });
  },

  beforeUnmount() {
    this.observers.forEach((observer) => observer.disconnect());
  },

  methods: {
    setupIntersectionObservers() {
      const options = { root: null, rootMargin: '0px', threshold: 0.5 };
      const elements = this.$refs.countElements;
      if (!elements) return;

      (elements as Element[]).forEach((element, index) => {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.animatedElements.has(index)) {
              this.updateCount(index);
              this.animatedElements.add(index);
              observer.unobserve(entry.target);
            }
          });
        }, options);
        observer.observe(element);
        this.observers.push(observer);
      });
    },

   
    parseNumericString(raw: unknown): { value: number; decimals: number } {
      const str = String(raw ?? '').replace(/\s+/g, '');
      const m = str.match(/-?\d+(?:[.,]\d+)?/);
      if (!m) return { value: 0, decimals: 0 };

      let numStr = m[0];
      if (numStr.includes('.') && numStr.includes(',')) {
        numStr = numStr.replace(/,/g, '');
      } else if (numStr.includes(',')) {
        numStr = numStr.replace(',', '.');
      }
      const decimals = numStr.includes('.') ? (numStr.split('.')[1] || '').length : 0;
      const value = parseFloat(numStr);
      if (Number.isNaN(value)) return { value: 0, decimals: 0 };
      return { value, decimals };
    },

   
    linearThenEaseOut(t: number, split = 0.8) {
      if (t <= split) {
       
        const head = (1 - (1 - split)) * (t / split); 
        return head; 
      } else {
       
        const u = (t - split) / (1 - split); // 0..1
        const easeOutCubic = 1 - Math.pow(1 - u, 3);
        return (1 - (1 - split)) + (1 - (1 - split)) * (0) + (1 - (1 - split)) * 0 + (1 - split) * easeOutCubic;
      
      }
    },

    
    animateNumber(
      el: HTMLElement,
      start: number,
      end: number,
      opts?: { duration?: number; delay?: number; decimals?: number; locale?: string; split?: number }
    ) {
      const duration = opts?.duration ?? 2000;
      const delay    = opts?.delay ?? 0;
      const decimals = Math.max(0, opts?.decimals ?? 0);
      const locale   = opts?.locale ?? 'tr-TR';
      const split    = Math.min(0.95, Math.max(0.55, opts?.split ?? 0.8)); 
      const reduced  = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

      const format = (v: number) =>
        (decimals > 0 ? Number(v.toFixed(decimals)) : Math.round(v)).toLocaleString(locale, {
          minimumFractionDigits: decimals, maximumFractionDigits: decimals
        });

      if (reduced || duration <= 0) {
        el.textContent = format(end);
        return;
      }

      let startTime: number | null = null;

      const step = (now: number) => {
        if (startTime === null) startTime = now + delay;
        const elapsed = now - startTime;
        if (elapsed < 0) { requestAnimationFrame(step); return; }

        const t = Math.min(1, elapsed / duration);
        const eased = t <= split
          ? (t / split) * split                   
          : split + (1 - split) * (1 - Math.pow(1 - (t - split) / (1 - split), 3)); 

       
        const cur = Math.max(start, start + (end - start) * Math.min(1, Math.max(0, eased)));

        const snap = t > 0.92 ? (decimals > 0 ? Number(cur.toFixed(decimals)) : Math.round(cur)) : cur;

        el.textContent = format(snap);

        if (t < 1) requestAnimationFrame(step);
        else el.textContent = format(end);
      };

      el.textContent = '';
      requestAnimationFrame(step);
    },

    updateCount(index: number) {
      const count = this.counts[index];
      const container = (this.$refs.countElements as Element[])[index];

      const jsCountEl = container.querySelector('.js-count') as HTMLElement | null;
      const odoV4El   = container.querySelector('.odometer-v4') as HTMLElement | null;
      const odoEl     = container.querySelector('.odometer') as HTMLElement | null;

    
      if (jsCountEl) {
        const { value: endValue, decimals } = this.parseNumericString(count?.title);
        this.animateNumber(jsCountEl, 0, endValue, { duration: 1600, decimals, locale: 'tr-TR', split: 0.82 });
        return;
      }

   
      if (odoV4El) {
        const { value: endValue, decimals } = this.parseNumericString(count?.title);
        const delay = index * 120;
        this.animateNumber(odoV4El, 0, endValue, { duration: 1900, delay, decimals, locale: 'tr-TR', split: 0.82 });
        return;
      }

      // v1–v2
      if (odoEl) {
        const { value: endValue, decimals } = this.parseNumericString(count?.title);
        this.animateNumber(odoEl, 0, endValue, { duration: 1800, decimals, locale: 'tr-TR', split: 0.8 });
      }
    },
  },
});
</script>

<style scoped>

:deep(.v4 .odometer-v4) {
  color: var(--c-primary) !important;
  font-weight: 400 !important;           
  font-variant-numeric: tabular-nums lining-nums;
  letter-spacing: 0.01em;
  text-rendering: geometricPrecision;
  -webkit-font-smoothing: auto;
  padding-right: 0.06em;                  
}
</style>
